import type { SearchDocument, SearchResult } from '@der-maschinfuehrer/catalog/worker-types';
import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent,
} from 'react';

import { WEB_CATALOG } from '../../catalog/generated-catalog';
import type { CatalogClient } from '../../catalog/catalog-client';
import { TextField } from '../../components/form-primitives';

interface CatalogAutocompleteFieldProps {
  readonly client: CatalogClient;
  readonly disabled?: boolean;
  readonly label: string;
  readonly name: string;
  readonly onChange: (value: string) => void;
  readonly onSelect: (document: SearchDocument) => void;
  readonly required?: boolean;
  readonly value: string;
}

interface CatalogQueryState {
  readonly error: string | null;
  readonly loading: boolean;
  readonly results: readonly SearchResult[];
  readonly value: string;
}

export const CatalogAutocompleteField = forwardRef<HTMLInputElement, CatalogAutocompleteFieldProps>(
  function CatalogAutocompleteField(
    { client, disabled, label, name, onChange, onSelect, required, value },
    forwardedRef,
  ) {
    const generatedId = useId();
    const inputId = `${name}-${generatedId}`;
    const listboxId = `${inputId}-suggestions`;
    const statusId = `${inputId}-catalog-status`;
    const input = useRef<HTMLInputElement | null>(null);
    const listbox = useRef<HTMLDivElement | null>(null);
    const querySequence = useRef(0);
    const suppressNextQuery = useRef(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [focused, setFocused] = useState(false);
    const [open, setOpen] = useState(false);
    const [queryState, setQueryState] = useState<CatalogQueryState>({
      error: null,
      loading: false,
      results: [],
      value: '',
    });
    const currentQuery =
      queryState.value === value ? queryState : { error: null, loading: false, results: [], value };
    const interactive = !disabled && focused;
    const results = interactive ? currentQuery.results : [];
    const loading = interactive && currentQuery.loading;
    const error = interactive ? currentQuery.error : null;
    const visibleOpen = interactive && open && results.length > 0;

    useEffect(() => {
      if (disabled || !focused) {
        querySequence.current += 1;
        return;
      }
      if (suppressNextQuery.current) {
        suppressNextQuery.current = false;
        return;
      }
      const sequence = ++querySequence.current;
      const timeout = setTimeout(() => {
        if (querySequence.current !== sequence) return;
        setQueryState({ error: null, loading: true, results: [], value });
        setOpen(false);
        setActiveIndex(-1);
        void client
          .query(value)
          .then((outcome) => {
            if (querySequence.current !== sequence) return;
            setQueryState({ error: null, loading: false, results: outcome.results, value });
            setActiveIndex(-1);
            setOpen(outcome.results.length > 0);
          })
          .catch((reason: unknown) => {
            if (querySequence.current !== sequence) return;
            setQueryState({
              error: reason instanceof Error ? reason.message : 'Našeptávač se nepodařilo načíst.',
              loading: false,
              results: [],
              value,
            });
            setOpen(false);
          });
      }, 120);
      return () => {
        clearTimeout(timeout);
        if (querySequence.current === sequence) querySequence.current += 1;
      };
    }, [client, disabled, focused, value]);

    const selectResult = (result: SearchResult) => {
      suppressNextQuery.current = true;
      querySequence.current += 1;
      setQueryState({
        error: null,
        loading: false,
        results: [],
        value: result.document.officialName,
      });
      setOpen(false);
      setActiveIndex(-1);
      onSelect(result.document);
      queueMicrotask(() => input.current?.focus());
    };

    const setInputRef = (node: HTMLInputElement | null) => {
      input.current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef !== null) {
        forwardedRef.current = node;
      }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape' && visibleOpen) {
        event.preventDefault();
        setOpen(false);
        setActiveIndex(-1);
        return;
      }
      if (event.key === 'ArrowDown' && results.length > 0) {
        event.preventDefault();
        setOpen(true);
        setActiveIndex((current) => (current + 1) % results.length);
        return;
      }
      if (event.key === 'ArrowUp' && results.length > 0) {
        event.preventDefault();
        setOpen(true);
        setActiveIndex((current) => (current <= 0 ? results.length - 1 : current - 1));
        return;
      }
      if (event.key === 'Enter' && visibleOpen && activeIndex >= 0) {
        const result = results[activeIndex];
        if (result !== undefined) {
          event.preventDefault();
          selectResult(result);
        }
      }
    };

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      suppressNextQuery.current = false;
      onChange(event.currentTarget.value);
    };

    const handleFocusLeave = (event: FocusEvent<HTMLDivElement>) => {
      if (
        event.relatedTarget instanceof Node &&
        event.currentTarget.contains(event.relatedTarget)
      ) {
        return;
      }
      setFocused(false);
      setOpen(false);
      setActiveIndex(-1);
    };

    const status = error
      ? `${error} Místo můžeš dál zadat ručně.`
      : loading
        ? value.trim().length === 0
          ? 'Načítám nedávná a oblíbená místa… Ruční zadání zůstává dostupné.'
          : 'Načítám návrhy míst… Ruční zadání zůstává dostupné.'
        : visibleOpen
          ? `${String(results.length)} návrhů. Šipkami vyber a potvrď Enterem, ` +
            'nebo pokračuj ručním zadáním.'
          : value.trim().length === 0 && focused
            ? 'Napiš název nebo kód. Místo můžeš vždy zadat ručně.'
            : 'Začni psát název nebo kód železničního místa. Ruční zadání je vždy dostupné.';

    return (
      <div className="catalog-combobox" onBlur={handleFocusLeave}>
        <div className="catalog-combobox-anchor">
          <TextField
            ref={setInputRef}
            id={inputId}
            label={label}
            name={name}
            value={value}
            maxLength={512}
            autoComplete="off"
            disabled={disabled}
            required={required}
            describedBy={statusId}
            role="combobox"
            aria-autocomplete="list"
            aria-busy={loading || undefined}
            aria-controls={visibleOpen ? listboxId : undefined}
            aria-expanded={visibleOpen}
            aria-activedescendant={
              visibleOpen && activeIndex >= 0
                ? `${listboxId}-option-${String(activeIndex)}`
                : undefined
            }
            onChange={handleChange}
            onFocus={() => {
              setFocused(true);
              if (results.length > 0) setOpen(true);
            }}
            onKeyDown={handleKeyDown}
          />

          {visibleOpen ? (
            <div ref={listbox} id={listboxId} className="catalog-suggestions" role="listbox">
              {results.map((result, index) => (
                <button
                  key={result.document.id}
                  id={`${listboxId}-option-${String(index)}`}
                  type="button"
                  className="catalog-suggestion touch-target"
                  role="option"
                  aria-selected={index === activeIndex}
                  onClick={() => selectResult(result)}
                  onFocus={() => setActiveIndex(index)}
                  onPointerMove={() => setActiveIndex(index)}
                >
                  <span className="catalog-suggestion-name">
                    <strong>
                      <HighlightedText
                        value={result.document.officialName}
                        ranges={
                          result.matchedField === 'official_name' &&
                          result.matchedValue === result.document.officialName
                            ? result.highlightRanges
                            : []
                        }
                      />
                    </strong>
                  </span>
                  <span className="catalog-suggestion-context">
                    {countryLabel(result.document.country)} · {result.document.region} ·{' '}
                    {locationTypeLabel(result.document.type)}
                  </span>
                  <span className="catalog-suggestion-codes">
                    {result.document.identifiers.length === 0
                      ? 'Bez evidovaného železničního kódu'
                      : result.document.identifiers.map((identifier, identifierIndex) => (
                          <span key={`${identifier.namespace}\u0000${identifier.normalizedValue}`}>
                            {identifierIndex > 0 ? ' · ' : null}
                            {identifier.namespace}{' '}
                            <HighlightedText
                              value={identifier.rawValue}
                              ranges={
                                result.matchedField === 'code' &&
                                result.matchedNamespace === identifier.namespace &&
                                result.matchedValue === identifier.rawValue
                                  ? result.highlightRanges
                                  : []
                              }
                            />
                          </span>
                        ))}
                  </span>
                  {matchedContextLabel(result) === null ? null : (
                    <span className="catalog-suggestion-match">
                      {matchedContextLabel(result)}:{' '}
                      <HighlightedText
                        value={result.matchedValue}
                        ranges={result.highlightRanges}
                      />
                    </span>
                  )}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <p
          id={statusId}
          className={error ? 'catalog-status catalog-status-error' : 'catalog-status'}
          role="status"
          aria-live="polite"
        >
          {status}
        </p>
        <p className="catalog-attribution">Katalog CZ + DE. {WEB_CATALOG.attribution}</p>
      </div>
    );
  },
);

function HighlightedText({
  value,
  ranges,
}: {
  readonly value: string;
  readonly ranges: SearchResult['highlightRanges'];
}) {
  const parts: Array<{ readonly highlighted: boolean; readonly value: string }> = [];
  let offset = 0;
  for (const range of [...ranges].sort((left, right) => left.start - right.start)) {
    const start = Math.max(offset, Math.min(value.length, range.start));
    const end = Math.max(start, Math.min(value.length, range.end));
    if (start > offset) parts.push({ highlighted: false, value: value.slice(offset, start) });
    if (end > start) parts.push({ highlighted: true, value: value.slice(start, end) });
    offset = end;
  }
  if (offset < value.length) parts.push({ highlighted: false, value: value.slice(offset) });
  if (parts.length === 0) return <>{value}</>;
  return (
    <>
      {parts.map((part, index) =>
        part.highlighted ? (
          <mark key={index}>{part.value}</mark>
        ) : (
          <span key={index}>{part.value}</span>
        ),
      )}
    </>
  );
}

function countryLabel(country: SearchDocument['country']): string {
  return country === 'CZ' ? 'Česko (CZ)' : 'Německo (DE)';
}

function locationTypeLabel(type: SearchDocument['type']): string {
  switch (type) {
    case 'station':
      return 'stanice';
    case 'halt':
      return 'zastávka';
    case 'junction':
      return 'odbočka';
    case 'boundary':
      return 'hraniční bod';
    case 'yard':
      return 'kolejiště / nákladní bod';
    case 'depot':
      return 'depo';
    case 'service_point':
      return 'provozní bod';
    case 'other':
      return 'jiné místo';
    case 'unknown':
      return 'neurčený typ';
  }
}

function matchedContextLabel(result: SearchResult): string | null {
  switch (result.matchedField) {
    case 'short_name':
      return 'Shoda v krátkém názvu';
    case 'alias':
      return 'Shoda v alternativním názvu';
    case 'historical':
      return 'Shoda v historickém názvu';
    case 'code':
    case 'official_name':
    case 'usage_context':
      return null;
  }
}
